import React, { useEffect, useRef, useState } from "react";
import { Tooltip } from "@mui/material";
import { FileDownloadOutlined, VisibilityOutlined } from "@mui/icons-material";
import { LineChart } from "@mui/x-charts/LineChart";
import classNames from "classnames";
import dayjs from "dayjs";
import axiosInstant from "../../api";
import { listProductPdpUrlScores } from "../../api/endpoints";
import { PdpUrlScorePoint } from "../../types/rmn";
import { DataTable, DataTableColumn } from "../DataTable/DataTable";
import { downloadPdpScorePdf } from "../PdpAudit/reportFiles";
import { scoreCls } from "./PdpScoreStatus";

// Score history for one PDP URL — the PDP counterpart of the Consumer Voice
// tab's history: a score-over-time chart, then a table of every completed
// run with view / download of its stored report. Always visible (the
// Product Pages tabs show one page at a time, so this is one request per
// visible page); refetches when a new ready score lands.

const SERIES = [
    { key: "overall_score" as const, label: "Overall", color: "var(--accent-text)" },
    { key: "human_conversion_score" as const, label: "Human", color: "#1C7A5A" },
    { key: "agentic_commerce_score" as const, label: "Agentic", color: "#2474DD" }
];

const icon = "w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 shrink-0";

const Score = ({ value }: { value: number | null }) => (
    <span className={classNames("text-[13px] font-bold tabular-nums", scoreCls(value))}>{value == null ? "—" : value}</span>
);

interface Props {
    productId: number;
    pdpUrlId: number;
    // The page's URL — names the downloaded PDF.
    pdpUrl: string;
    // The row's latest ready score id — when it changes (a run just
    // finished), the history refetches so the new run appears.
    latestScoreId?: number | null;
    onViewReport: (scoreId: number) => void;
    className?: string;
}

export function PdpScoreHistory({ productId, pdpUrlId, pdpUrl, latestScoreId, onViewReport, className }: Props) {
    const [points, setPoints] = useState<PdpUrlScorePoint[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const loadedForScoreId = useRef<number | null | undefined>(undefined);

    useEffect(() => {
        // Fetch on mount, and again only when a new ready score lands.
        if (loadedForScoreId.current === latestScoreId && points !== null) return;
        loadedForScoreId.current = latestScoreId;
        setLoading(true);
        axiosInstant
            .get(listProductPdpUrlScores(productId, pdpUrlId))
            .then((res) => {
                const { data } = res;
                setPoints((Array.isArray(data) ? data : data?.data || []) as PdpUrlScorePoint[]);
                setError(null);
            })
            .catch(() => setError("Couldn't load the score history. Please try again."))
            .finally(() => setLoading(false));
    }, [latestScoreId, productId, pdpUrlId]);

    const chartPoints = (points || []).filter((p) => p.scored_at);
    // Newest first in the table; the chart wants chronological.
    const rows = [...chartPoints].reverse();

    const columns: DataTableColumn<PdpUrlScorePoint>[] = [
        {
            key: "date",
            header: "Scored",
            render: (p) => (
                <span className="text-[12px] text-text-2 whitespace-nowrap">{dayjs(p.scored_at).format("MMM DD, YYYY h:mm A")}{p.scoring_version === "v1" && <span className="ml-1.5" title="Scored under the earlier method (totals written by the model)">v1</span>}</span>
            )
        },
        { key: "overall", header: "Overall", width: 90, render: (p) => <Score value={p.overall_score} /> },
        { key: "human", header: "Human", width: 90, render: (p) => <Score value={p.human_conversion_score} /> },
        { key: "agentic", header: "Agentic", width: 90, render: (p) => <Score value={p.agentic_commerce_score} /> },
        {
            key: "actions",
            header: "",
            render: (p) => (
                <div className="flex items-center justify-end gap-1">
                    <Tooltip title="View this report">
                        <button type="button" aria-label="View report" className={icon} onClick={() => onViewReport(p.id)}>
                            <VisibilityOutlined sx={{ fontSize: 15 }} />
                        </button>
                    </Tooltip>
                    <Tooltip title="Download this report (PDF)">
                        <button
                            type="button"
                            aria-label="Download report"
                            className={icon}
                            onClick={() => downloadPdpScorePdf({ productId, pdpUrlId, scoreId: p.id, url: pdpUrl })}
                        >
                            <FileDownloadOutlined sx={{ fontSize: 15 }} />
                        </button>
                    </Tooltip>
                </div>
            )
        }
    ];

    return (
        <div className={className}>
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-[16px] font-bold text-text-1">Score History</h2>
                {chartPoints.length > 0 && <span className="text-[12px] text-muted">{chartPoints.length} total</span>}
            </div>

            {!loading && !error && chartPoints.length >= 2 && (
                <div className="border border-border-token rounded-[11px] bg-surface-2 px-4 py-3 mb-4">
                    <div className="text-[12px] font-semibold text-text-2 mb-1">Score over time</div>
                    <LineChart
                        height={220}
                        xAxis={[
                            {
                                scaleType: "time",
                                data: chartPoints.map((p) => new Date(p.scored_at)),
                                valueFormatter: (d: Date) => dayjs(d).format("MMM DD")
                            }
                        ]}
                        yAxis={[{ min: 0, max: 100 }]}
                        series={SERIES.map((s) => ({
                            label: s.label,
                            color: s.color,
                            curve: "monotoneX" as const,
                            showMark: true,
                            data: chartPoints.map((p) => p[s.key])
                        }))}
                        margin={{ top: 40, right: 16, bottom: 24, left: 36 }}
                    />
                </div>
            )}

            <DataTable<PdpUrlScorePoint>
                columns={columns}
                rows={rows}
                loading={loading}
                totalCount={rows.length}
                hasMore={false}
                error={error}
                onLoadMore={() => undefined}
                emptyMessage="No completed scores yet — the first one lands a few minutes after the page is added."
                getRowKey={(p) => p.id}
            />
        </div>
    );
}
